import { Separator, Text } from '@cuemath/web-comps';
import React from 'react';
import {
  Button,
  Form,
  Icon,
  Input,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
} from 'semantic-ui-react';

import COLORS from '../../constants/colors';
import { usePostEvalHandoffTrigger } from 'actions/eval-student-handoff';
import { isUuid } from './constants';

const TriggerHandoff = ({ closeModal }) => {
  const [enrollmentIdInput, setEnrollmentIdInput] = React.useState('');
  const [teacherIdInput, setTeacherIdInput] = React.useState('');
  const [triggerError, setTriggerError] = React.useState(null);
  const [triggerResult, setTriggerResult] = React.useState(null);

  const { post: postTrigger, isProcessing: isTriggering } =
    usePostEvalHandoffTrigger({
      // Stay open on success: the coach and task ids are what the AC came for.
      onComplete: (error, data) => {
        if (error) {
          setTriggerError(error || 'Could not raise the handoff task.');
          return;
        }
        setTriggerError(null);
        setTriggerResult(data || {});
      },
    });

  const enrollmentId = enrollmentIdInput.trim();
  const teacherId = teacherIdInput.trim();
  const canTrigger = isUuid(enrollmentId) && isUuid(teacherId) && !isTriggering;

  const handleTrigger = React.useCallback(() => {
    setTriggerError(null);
    postTrigger({ teacher_id: teacherId }, enrollmentId);
  }, [postTrigger, teacherId, enrollmentId]);

  const renderSuccess = () => (
    <Segment>
      <Text variant="body1Medium" color={COLORS.DARK_GREEN}>
        <Icon name="check circle" />
        {triggerResult.already_raised
          ? ' This coach already had the handoff task.'
          : ' Handoff task raised.'}
      </Text>
      <Separator height={16} transparent />
      <Text variant="body1Medium">Coach</Text>
      <Text variant="body1Regular">{triggerResult.teacher_id || '-'}</Text>
      <Separator height={12} transparent />
      <Text variant="body1Medium">Task</Text>
      <Text variant="body1Regular">{triggerResult.task_id || '-'}</Text>
      {triggerResult.reassigned_from && (
        <>
          <Separator height={12} transparent />
          <Text variant="body1Medium">Moved from</Text>
          <Text variant="body1Regular">{triggerResult.reassigned_from}</Text>
          <Separator height={8} transparent />
          <Text variant="body3" color={COLORS.GRAY}>
            The student was on another coach, who has been replaced.
          </Text>
        </>
      )}
      {triggerResult.created_mapping && (
        <>
          <Separator height={12} transparent />
          <Text variant="body3" color={COLORS.GRAY}>
            No mapping existed for this pair, so one was written against this
            coach.
          </Text>
        </>
      )}
    </Segment>
  );

  const renderForm = () => (
    <Form>
      <Text variant="body1Medium">Intel Enrollment ID</Text>
      <Separator height={4} transparent />
      <Input
        fluid
        placeholder="Paste the intel enrollment UUID"
        value={enrollmentIdInput}
        onChange={(e, { value }) => {
          setEnrollmentIdInput(value);
          setTriggerError(null);
        }}
        aria-label="Intel enrollment ID"
      />
      <Separator height={12} transparent />
      <Text variant="body1Medium">Coach ID</Text>
      <Separator height={4} transparent />
      <Input
        fluid
        placeholder="Paste the coach's teacher UUID"
        value={teacherIdInput}
        onChange={(e, { value }) => {
          setTeacherIdInput(value);
          setTriggerError(null);
        }}
        aria-label="Coach ID"
      />
      <Separator height={12} transparent />
      <Text variant="body3" color={COLORS.GRAY}>
        Naming a coach the student is not already on moves them to this coach
        first. The student needs a completed eval, and the training tool has to
        accept them, or no task is raised.
      </Text>
    </Form>
  );

  return (
    <Modal open size="small" onClose={closeModal}>
      <ModalHeader>
        <Text variant="h6">Trigger Student Handoff</Text>
        <Text variant="body3" color={COLORS.GRAY}>
          Raise a coach&apos;s prep card when the enrollment never did.
        </Text>
      </ModalHeader>
      <ModalContent scrolling>
        <ModalDescription>
          {triggerResult ? renderSuccess() : renderForm()}
          {triggerError && (
            <>
              <Separator height={16} transparent />
              <Text variant="body2" color={COLORS.RUSTY_RED}>
                {triggerError}
              </Text>
            </>
          )}
        </ModalDescription>
      </ModalContent>
      <ModalActions>
        {triggerResult ? (
          <Button color="black" onClick={closeModal}>
            Done
          </Button>
        ) : (
          <>
            <Button basic onClick={closeModal}>
              Cancel
            </Button>
            <Button
              color="black"
              loading={isTriggering}
              disabled={!canTrigger}
              onClick={handleTrigger}
            >
              Trigger Handoff
            </Button>
          </>
        )}
      </ModalActions>
    </Modal>
  );
};

export default TriggerHandoff;
